import { defineStore } from "pinia"
import { ref } from "vue"
import type { BaseModel } from "@/models/base.model"

export const useEntitySaveStateStore = defineStore("entitySaveStateStore", () => {
  const creationStates = ref<Map<string, boolean>>(new Map())
  const updateStates = new Map<string, UpdateState<unknown>>()

  type UpdateState<T> = {
    isUpdating: boolean
    pendingUpdate?: PendingUpdate<T>
  }

  type PendingUpdate<T> = {
    update: () => Promise<T>
    resolve: (value: T) => void
    reject: (reason?: unknown) => void
  }

  function setCreationState(entity: BaseModel, isCreating: boolean) {
    creationStates.value.set(entity.id!, isCreating)
  }

  function isCreating(entity: BaseModel) {
    return creationStates.value.get(entity.id!) || false
  }

  /**
   * Runs one update per persisted entity at a time. While an update is in flight, keep only the
   * most recent pending update; intermediate full-entity snapshots are obsolete. Unrelated
   * entities can still update concurrently.
   */
  function enqueueLatestUpdate<T>(entity: BaseModel, update: () => Promise<T>): Promise<T> {
    const entityId = entity.id!
    let updateState = updateStates.get(entityId) as UpdateState<T> | undefined

    if (!updateState) {
      updateState = { isUpdating: false }
      updateStates.set(entityId, updateState as UpdateState<unknown>)
    }

    if (!updateState.isUpdating) {
      updateState.isUpdating = true
      return runUpdate(entityId, updateState, update)
    }

    return new Promise<T>((resolve, reject) => {
      const previousPendingUpdate = updateState.pendingUpdate
      updateState.pendingUpdate = {
        update,
        resolve: (value) => {
          previousPendingUpdate?.resolve(value)
          resolve(value)
        },
        reject: (reason) => {
          previousPendingUpdate?.reject(reason)
          reject(reason)
        }
      }
    })
  }

  async function runUpdate<T>(
    entityId: string,
    updateState: UpdateState<T>,
    update: () => Promise<T>
  ): Promise<T> {
    try {
      return await update()
    } finally {
      const pendingUpdate = updateState.pendingUpdate
      updateState.pendingUpdate = undefined

      if (pendingUpdate) {
        void runUpdate(entityId, updateState, pendingUpdate.update).then(
          pendingUpdate.resolve,
          pendingUpdate.reject
        )
      } else {
        updateState.isUpdating = false
        updateStates.delete(entityId)
      }
    }
  }

  return {
    creationStates,
    setCreationState,
    isCreating,
    enqueueLatestUpdate
  }
})
